
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AuthUser } from "./types";
import { useAuthFunctions } from "./useAuthFunctions";

export const useAuthState = () => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  const authFunctions = useAuthFunctions(setUser, setIsLoading);
  
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (session?.user) {
          setUser({
            id: session.user.id,
            email: session.user.email || "",
            name: session.user.user_metadata.name || session.user.email?.split("@")[0] || "",
            companyName: session.user.user_metadata.companyName || "",
          });
          
          authFunctions.fetchUserRoles(session.user.id);
        } else {
          setUser(null);
        }
        setIsLoading(false);
        setError(null);
      }
    );

    checkUser();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const checkUser = async () => {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) {
        throw sessionError;
      }
      
      if (session?.user) {
        setUser({
          id: session.user.id,
          email: session.user.email || "",
          name: session.user.user_metadata.name || session.user.email?.split("@")[0] || "",
          companyName: session.user.user_metadata.companyName || "",
        });
        
        authFunctions.fetchUserRoles(session.user.id);
      }
      setError(null);
    } catch (err) {
      console.error("Error checking user session:", err);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  };

  return {
    user,
    setUser,
    isLoading,
    setIsLoading,
    error,
    setError,
    ...authFunctions
  };
};
